import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../../firebase/firebase";
import { useAuth } from "../../context/AuthContext";
import Header from "../../components/Header/Header";
import StepCard from "../Signup/StepCard";
import "../Signup/signup.css";

/* ---------------------------------------------
   Shared industry master list
--------------------------------------------- */
const INDUSTRIES = [
  "HealthTech",
  "MedTech",
  "BioTech",
  "EdTech",
  "FinTech",
  "InsurTech",
  "ClimateTech",
  "CleanTech / Energy",
  "AI / Machine Learning",
  "SaaS / B2B Software",
  "Developer Tools",
  "DeepTech",
  "AgriTech",
  "FoodTech",
  "E-commerce / Marketplaces",
  "Supply Chain / Logistics",
  "Cybersecurity",
  "LegalTech",
  "HRTech / Future of Work",
  "Consumer Tech / D2C",
];

/* ---------------------------------------------
   Evaluator onboarding steps
--------------------------------------------- */
const steps = [
  {
    title: "Your details",
    question: "What is your full name?",
    key: "fullName",
    maxLength: 60,
  },
  {
    title: "Your role",
    question: "What best describes your role?",
    key: "title",
    maxLength: 60,
  },
  {
    title: "Organization",
    question: "Organization / fund / accelerator name",
    key: "organization",
    maxLength: 80,
  },
  {
    title: "Focus areas",
    question: "Select up to 5 industries you actively evaluate",
    key: "industries",
  },
  {
    title: "Website",
    question: "Website or LinkedIn (optional)",
    key: "website",
    maxLength: 120,
  },
];

function EvaluatorOnboarding() {
  const { user, refreshProfile } = useAuth();
  const navigate = useNavigate();

  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({ industries: [] });
  const [saving, setSaving] = useState(false);
  const [checking, setChecking] = useState(true);

  const step = steps[currentStep];

  /* ---------------------------------------------
     Redirect ONLY if onboarding already completed
  --------------------------------------------- */
  useEffect(() => {
    const checkExistingProfile = async () => {
      const snap = await getDoc(doc(db, "users", user.uid));

      if (snap.exists() && snap.data()?.evaluatorOnboarded === true) {
        navigate("/evaluator", { replace: true });
        return;
      }

      setChecking(false);
    };

    checkExistingProfile();
  }, [user, navigate]);

  if (checking) return null;

  /* ---------------------------------------------
     Industry selection (max 5)
  --------------------------------------------- */
  const toggleIndustry = (industry) => {
    setFormData((prev) => {
      const selected = prev.industries || [];

      if (selected.includes(industry)) {
        return {
          ...prev,
          industries: selected.filter((i) => i !== industry),
        };
      }

      if (selected.length >= 5) return prev;

      return {
        ...prev,
        industries: [...selected, industry],
      };
    });
  };

  /* ---------------------------------------------
     Next / Save handler
  --------------------------------------------- */
  const handleNext = async () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep((s) => s + 1);
      return;
    }

    try {
      setSaving(true);

      await setDoc(
        doc(db, "users", user.uid),
        {
          ...formData,
          persona: "evaluator",
          evaluatorOnboarded: true,
          plan: "free",
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      await refreshProfile();
      navigate("/evaluator", { replace: true });
    } catch {
      alert("Failed to save profile.");
    } finally {
      setSaving(false);
    }
  };

  /* ---------------------------------------------
     Custom Industry Step UI
  --------------------------------------------- */
  const renderIndustryStep = () => (
    <div className="step-card">
      <h2>{step.title}</h2>
      <p className="question-text">{step.question}</p>

      <div className="industry-grid">
        {INDUSTRIES.map((industry) => {
          const selected = formData.industries.includes(industry);
          const disabled = !selected && formData.industries.length >= 5;

          return (
            <button
              key={industry}
              type="button"
              className={`industry-chip ${selected ? "selected" : ""}`}
              disabled={disabled}
              onClick={() => toggleIndustry(industry)}
            >
              {industry}
            </button>
          );
        })}
      </div>

      <div className="step-actions">
        <button
          className="secondary-button"
          onClick={() => setCurrentStep((s) => s - 1)}
        >
          Back
        </button>

        <button
          className="primary-button"
          disabled={formData.industries.length === 0}
          onClick={handleNext}
        >
          Finish
        </button>
      </div>
    </div>
  );

  return (
    <>
      <Header />

      <div className="signup-container">
        <div className="onboarding-wrapper">
          {step.key === "industries" ? (
            renderIndustryStep()
          ) : (
            <StepCard
              title={step.title}
              question={step.question}
              value={formData[step.key] || ""}
              onChange={(v) =>
                setFormData((prev) => ({ ...prev, [step.key]: v }))
              }
              onNext={handleNext}
              onBack={() => setCurrentStep((s) => s - 1)}
              isFirst={currentStep === 0}
              isLast={currentStep === steps.length - 1}
              maxLength={step.maxLength}
            />
          )}

          {saving && <p className="saving-text">Saving profile…</p>}
        </div>
      </div>
    </>
  );
}

export default EvaluatorOnboarding;
